import React, { useState } from 'react';
import { Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { enqueueSnackbar } from 'notistack';
import { useLogAppAction } from 'hooks/useLogAppAction';
import { ExportHubAPI, ExportProduct } from 'api/ExportHubAPI';

interface Props {
  open: boolean;
  /** The product to delete; null keeps the modal closed. */
  product: Pick<ExportProduct, 'id' | 'name' | 'internal_sku'> | null;
  groupId: string | undefined;
  onClose: () => void;
  /** Called after a successful delete, so the caller can refresh or navigate. */
  onDeleted: () => void;
}

/**
 * Confirms an irreversible product delete. The product's screenings,
 * classification results, license links and BOM edges cascade with it, so the
 * modal names exactly what goes before the user commits.
 */
const DeleteProductModal: React.FC<Props> = ({
  open,
  product,
  groupId,
  onClose,
  onDeleted,
}) => {
  const [deleting, setDeleting] = useState(false);
  const logAppAction = useLogAppAction();

  const handleClose = (): void => {
    if (deleting) return;
    onClose();
  };

  const confirm = async (): Promise<void> => {
    if (!product || !groupId) return;
    logAppAction('export_hub:delete_product');
    setDeleting(true);
    try {
      await ExportHubAPI.deleteProduct(product.id, groupId);
      enqueueSnackbar(`Deleted "${product.name}".`, { variant: 'success' });
      onDeleted();
    } catch (err: any) {
      console.error('Failed to delete product:', err);
      enqueueSnackbar(
        err?.response?.data?.message || 'Failed to delete this product.',
        { variant: 'error' },
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} className='eh-modal-root'>
      <div className='eh-modal' role='dialog' aria-label='Delete product'>
        <div className='eh-modal-head'>
          <h2 className='eh-modal-title'>Delete Product</h2>
          <button
            className='eh-modal-close'
            onClick={handleClose}
            aria-label='Close'
            type='button'
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className='eh-modal-body'>
          <div className='eh-cov-banner eh-sev-box-danger'>
            <WarningAmberIcon
              className='eh-sev-danger'
              style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}
            />
            <span>
              This permanently deletes{' '}
              <strong>{product?.name || 'this product'}</strong>
              {product?.internal_sku ? ` (${product.internal_sku})` : ''} along
              with its screenings, classification results, license links and
              hierarchy links. This cannot be undone.
            </span>
          </div>
        </div>

        <div className='eh-modal-foot'>
          <button
            className='btn-outline'
            type='button'
            onClick={handleClose}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            className='btn-outline eh-btn-danger'
            type='button'
            onClick={confirm}
            disabled={deleting}
            style={{ opacity: deleting ? 0.6 : 1 }}
          >
            {deleting ? 'Deleting…' : 'Delete Product'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteProductModal;
